import apiService from './api';
import type { ApiResponse } from '../types';
import type {
  Call, CallerLookup, CrmAttach, CrmFile, CrmSubject, CustomerNote, FollowUp, FollowUpChanges, FollowUpDetail,
  FollowUpFilter, FollowUpKind, NewFollowUp, PhoneDomain,
  PhoneDomainWithSecret, PhoneExtension, SalesPerson, SalesSettings, SaveExtensionPayload, Voicemail, VoicemailFilter,
} from '../types/sales';

/**
 * The Sales module: the phone system connection, caller lookup, the call
 * log, voicemails, and the CRM (customer notes and follow-ups). All under
 * /api/sales (HHNodeServer routes/sales.js).
 */
class SalesService {
  // ── Phone system ──

  async listDomains(): Promise<ApiResponse<PhoneDomain[]>> {
    return apiService.get('/sales/phone/domains');
  }

  async addDomain(label: string, domainName: string): Promise<ApiResponse<PhoneDomainWithSecret>> {
    return apiService.post('/sales/phone/domains', { label, domain_name: domainName });
  }

  async updateDomain(id: number, changes: { label?: string; active?: boolean }): Promise<ApiResponse<PhoneDomain>> {
    return apiService.put(`/sales/phone/domains/${id}`, changes);
  }

  /** A new secret. The bridge stops being accepted until it has the new one. */
  async rotateSecret(id: number): Promise<ApiResponse<PhoneDomainWithSecret>> {
    return apiService.post(`/sales/phone/domains/${id}/rotate-secret`, {});
  }

  async listExtensions(domainId: number): Promise<ApiResponse<PhoneExtension[]>> {
    return apiService.get(`/sales/phone/domains/${domainId}/extensions`);
  }

  async addExtension(domainId: number, payload: SaveExtensionPayload): Promise<ApiResponse<{ id: number }>> {
    return apiService.post(`/sales/phone/domains/${domainId}/extensions`, payload);
  }

  async updateExtension(id: number, payload: SaveExtensionPayload): Promise<ApiResponse<{ id: number }>> {
    return apiService.put(`/sales/phone/extensions/${id}`, payload);
  }

  async deleteExtension(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/sales/phone/extensions/${id}`);
  }

  async listPeople(): Promise<ApiResponse<SalesPerson[]>> {
    return apiService.get('/sales/phone/people');
  }

  // ── Callers and calls ──

  async lookup(number: string): Promise<ApiResponse<CallerLookup>> {
    return apiService.get(`/sales/phone/lookup?number=${encodeURIComponent(number)}`);
  }

  /** Switch recording on (callers must be told first) or off. */
  async setRecording(domainId: number, enabled: boolean, callersTold = false): Promise<ApiResponse<PhoneDomain>> {
    return apiService.put(`/sales/phone/domains/${domainId}/recording`, enabled ? { enabled, callers_told: callersTold } : { enabled });
  }

  async getSettings(): Promise<ApiResponse<SalesSettings>> {
    return apiService.get('/sales/settings');
  }

  async saveSettings(retentionMonths: number | null): Promise<ApiResponse<SalesSettings>> {
    return apiService.put('/sales/settings', { retention_months: retentionMonths });
  }

  async listCalls(opts: { customerId?: number; limit?: number } = {}): Promise<ApiResponse<Call[]>> {
    const q = new URLSearchParams();
    if (opts.customerId) q.set('customer_id', String(opts.customerId));
    if (opts.limit) q.set('limit', String(opts.limit));
    const qs = q.toString();
    return apiService.get(`/sales/calls${qs ? `?${qs}` : ''}`);
  }

  /** A recording's audio, for someone who may hear it. */
  async callRecording(callId: number): Promise<Blob> {
    return apiService.get(`/sales/calls/${callId}/recording`, { responseType: 'blob', timeout: 60000 }) as unknown as Promise<Blob>;
  }

  // ── Voicemail ──

  async listVoicemails(filter: VoicemailFilter = 'open', customerId?: number): Promise<ApiResponse<Voicemail[]>> {
    const q = new URLSearchParams({ filter });
    if (customerId) q.set('customer_id', String(customerId));
    return apiService.get(`/sales/voicemails?${q.toString()}`);
  }

  async assignVoicemail(id: number, userId: number | null): Promise<ApiResponse<Voicemail>> {
    return apiService.put(`/sales/voicemails/${id}/assign`, { user_id: userId });
  }

  async followUpVoicemail(id: number, note: string): Promise<ApiResponse<Voicemail>> {
    return apiService.post(`/sales/voicemails/${id}/follow-up`, { note });
  }

  async reopenVoicemail(id: number): Promise<ApiResponse<Voicemail>> {
    return apiService.post(`/sales/voicemails/${id}/reopen`, {});
  }

  async voicemailAudio(id: number): Promise<Blob> {
    return apiService.get(`/sales/voicemails/${id}/audio`, { responseType: 'blob', timeout: 60000 }) as unknown as Promise<Blob>;
  }

  // ── CRM: notes, follow-ups and issues ──

  /** The people a follow-up or issue may be given to: a Sales seat, or a Nursery operator and up. */
  async listCrmPeople(): Promise<ApiResponse<SalesPerson[]>> {
    return apiService.get('/sales/crm/people');
  }

  async listNotes(subject: CrmSubject): Promise<ApiResponse<CustomerNote[]>> {
    return apiService.get(`/sales/notes?${subjectQuery(subject)}`);
  }

  async addNote(note: CrmAttach & { body: string; pinned?: boolean; call_id?: number; voicemail_id?: number }): Promise<ApiResponse<CustomerNote>> {
    return apiService.post('/sales/notes', note);
  }

  async updateNote(id: number, changes: { body?: string; pinned?: boolean }): Promise<ApiResponse<CustomerNote>> {
    return apiService.put(`/sales/notes/${id}`, changes);
  }

  async removeNote(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/sales/notes/${id}`);
  }

  async listFollowUps(
    filter: FollowUpFilter = 'mine',
    opts: { subject?: CrmSubject; kind?: FollowUpKind | null } = {},
  ): Promise<ApiResponse<FollowUp[]>> {
    const q = new URLSearchParams({ filter });
    if (opts.kind) q.set('kind', opts.kind);
    const about = opts.subject ? `&${subjectQuery(opts.subject)}` : '';
    return apiService.get(`/sales/follow-ups?${q.toString()}${about}`);
  }

  async getFollowUp(id: number): Promise<ApiResponse<FollowUpDetail>> {
    return apiService.get(`/sales/follow-ups/${id}`);
  }

  async createFollowUp(payload: NewFollowUp): Promise<ApiResponse<FollowUp>> {
    return apiService.post('/sales/follow-ups', payload);
  }

  async updateFollowUp(id: number, changes: FollowUpChanges): Promise<ApiResponse<FollowUp>> {
    return apiService.put(`/sales/follow-ups/${id}`, changes);
  }

  async completeFollowUp(id: number, note: string): Promise<ApiResponse<FollowUp>> {
    return apiService.post(`/sales/follow-ups/${id}/done`, { note });
  }

  async reopenFollowUp(id: number): Promise<ApiResponse<FollowUp>> {
    return apiService.post(`/sales/follow-ups/${id}/reopen`, {});
  }

  async addUpdate(id: number, body: string): Promise<ApiResponse<{ id: number; follow_up_id: number }>> {
    return apiService.post(`/sales/follow-ups/${id}/updates`, { body });
  }

  async setWatching(id: number, watching: boolean): Promise<ApiResponse<{ watching: boolean }>> {
    return apiService.put(`/sales/follow-ups/${id}/watch`, { watching });
  }

  /** A photo onto a note, a follow-up or an update. A phone photo: well past the default 10 s. */
  async attachFile(kind: 'notes' | 'follow-ups' | 'updates', id: number, file: File): Promise<ApiResponse<CrmFile>> {
    const form = new FormData();
    form.append('file', file);
    return apiService.post(`/sales/${kind}/${id}/files`, form, undefined, { timeout: 120000 });
  }

  async fileBlob(id: number): Promise<Blob> {
    return apiService.get(`/sales/files/${id}`, { responseType: 'blob', timeout: 60000 }) as unknown as Promise<Blob>;
  }

  async removeFile(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/sales/files/${id}`);
  }

  /** The people with a Sales seat, to give a voicemail or a follow-up to. */
  async listSalesPeople(): Promise<ApiResponse<SalesPerson[]>> {
    return apiService.get('/sales/people');
  }
}

function subjectQuery(subject: CrmSubject): string {
  if ('customerId' in subject) return `customer_id=${subject.customerId}`;
  if ('orderId' in subject) return `order_id=${subject.orderId}`;
  return `load_id=${subject.loadId}`;
}

const salesService = new SalesService();
export default salesService;
